import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router';

import BaseModal, { type ModalAction } from '../../components/ui/base-modal';

type AlertModalConfig = {
  kind: 'alert';
  id?: string;
  title?: string;
  content: ReactNode;
  closeLabel?: string;
  onClose?: () => void;
  headerClose?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  actionsClassName?: string;
};

type ConfirmModalConfig = {
  kind: 'confirm';
  id?: string;
  title?: string;
  content: ReactNode;
  confirmLabel?: string;
  onConfirm: () => void;
  cancelLabel?: string;
  onCancel?: () => void;
  headerClose?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
};

type CustomModalConfig = {
  kind: 'custom';
  id?: string;
  title?: string;
  content: ReactNode;
  actions: ModalAction[];
  headerClose?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl';
};

export type ModalConfig =
  | AlertModalConfig
  | ConfirmModalConfig
  | CustomModalConfig;

type ModalState = {
  id: string;
  config: ModalConfig;
};

const ModalContext = createContext<
  | {
      openAlert: (config: Omit<AlertModalConfig, 'kind'>) => string;
      openConfirm: (config: Omit<ConfirmModalConfig, 'kind'>) => string;
      openCustom: (config: Omit<CustomModalConfig, 'kind'>) => string;
      close: (id: string) => void;
      closeAll: () => void;
    }
  | undefined
>(undefined);

export const ModalProvider = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  const previousLocationKeyRef = useRef(location.key);
  const [stack, setStack] = useState<ModalState[]>([]);

  const openAlert = (config: Omit<AlertModalConfig, 'kind'>) => {
    const id =
      config.id || crypto.randomUUID?.() || (stack.length + 1).toString();
    const alertModalConfig: AlertModalConfig = {
      ...config,
      kind: 'alert',
      id,
    };
    setStack(prevStack => [...prevStack, { id, config: alertModalConfig }]);
    return id;
  };

  const openConfirm = (config: Omit<ConfirmModalConfig, 'kind'>) => {
    const id =
      config.id || crypto.randomUUID?.() || (stack.length + 1).toString();
    const confirmModalConfig: ConfirmModalConfig = {
      ...config,
      kind: 'confirm',
      id,
    };

    setStack(prevStack => [...prevStack, { id, config: confirmModalConfig }]);
    return id;
  };

  const openCustom = (config: Omit<CustomModalConfig, 'kind'>) => {
    const id =
      config.id || crypto.randomUUID?.() || (stack.length + 1).toString();
    const customModalConfig: CustomModalConfig = {
      ...config,
      kind: 'custom',
      id,
    };

    setStack(prevStack => [...prevStack, { id, config: customModalConfig }]);
    return id;
  };

  const close = (id: string) => {
    setStack(prevStack => prevStack.filter(config => config.id !== id));
  };

  const closeAll = () => {
    setStack([]);
  };

  useEffect(() => {
    if (previousLocationKeyRef.current === location.key) return;

    previousLocationKeyRef.current = location.key;
    closeAll();
  }, [location.key]);

  const active = stack[stack.length - 1];
  const actions: ModalAction[] = [];
  if (active?.config.kind === 'alert') {
    const onClose = active.config.onClose;
    actions.push({
      id: 'close',
      variant: 'primary',
      label: active.config.closeLabel || 'OK',
      onClick: () => {
        onClose?.();
        close(active.id);
      },
    });
  } else if (active?.config.kind === 'confirm') {
    const onCancel = active.config.onCancel;
    actions.push({
      id: 'cancel',
      variant: 'secondary',
      label: active.config.cancelLabel || 'Cancel',
      onClick: () => {
        onCancel?.();
        close(active.id);
      },
    });

    const onConfirm = active.config.onConfirm;
    actions.push({
      id: 'confirm',
      variant: 'primary',
      label: active.config.confirmLabel || 'OK',
      onClick: () => {
        onConfirm();
        close(active.id);
      },
    });
  } else if (active?.config.kind === 'custom') {
    active.config.actions.forEach(action => {
      actions.push({
        ...action,
        onClick: e => {
          if (action.onClick) {
            action.onClick(e, active.id);
            // close(active.id);
          }
        },
      });
    });
  }

  return (
    <ModalContext.Provider
      value={{ openAlert, openConfirm, openCustom, close, closeAll }}
    >
      {children}
      {active && (
        <BaseModal
          key={active.id}
          title={active.config.title}
          actions={actions}
          onClose={close.bind(this, active.id)}
          headerClose={active.config.headerClose}
          size={active.config.size}
          className={
            active.config.kind === 'alert' ? active.config.className : undefined
          }
          actionsClassName={
            active.config.kind === 'alert'
              ? active.config.actionsClassName
              : undefined
          }
        >
          {active.config.content}
        </BaseModal>
      )}
    </ModalContext.Provider>
  );
};

const useModal = () => {
  const context = useContext(ModalContext);

  return context!;
};

export default useModal;
